import { Link } from "react-router-dom";
import { ArrowRight, Eye, HandHeart, ShieldCheck, Headphones } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";

const PILLARS = [
  {
    icon: Eye,
    title: "Clarity",
    desc: "Product pages should identify the exact item, strength, pack size, price and market availability.",
  },
  {
    icon: HandHeart,
    title: "Care",
    desc: "Customer questions should be handled respectfully and directed to appropriately qualified professionals when clinical guidance is needed.",
  },
  {
    icon: ShieldCheck,
    title: "Responsibility",
    desc: "Product listings, images and fulfilment processes should be verified and consistent with applicable laws in every market served.",
  },
  {
    icon: Headphones,
    title: "Support",
    desc: "Customers can contact our team for help with product information and order-related enquiries.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "About OneCare Pharmacy",
  url: "https://onecarelife.health/about",
};

export default function About() {
  return (
    <main className="bg-[#F8FAFC]">
      <Seo
        title="About OneCare Pharmacy | Our Customer-First Approach"
        description="Learn about OneCare Pharmacy, our approach to clear product information, customer support and responsible healthcare service."
        path="/about"
        jsonLd={jsonLd}
      />

      <section className="bg-white pb-16 pt-16 sm:pt-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <p className="font-mono text-xs font-semibold tracking-[0.22em] text-emerald-700">ABOUT US</p>
          <h1 className="mt-3 max-w-3xl font-display text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Healthcare information with a customer-first approach
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            OneCare Pharmacy aims to make healthcare product information easier to understand and customer support
            easier to reach. We focus on clear catalogue details, responsive communication and a responsible approach
            to healthcare-related enquiries.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Our approach</h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div
                  data-testid={`approach-card-${p.title.toLowerCase()}`}
                  className="h-full rounded-2xl border border-slate-200 bg-white p-8 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-600/30 hover:shadow-lg"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#D8EFE6] text-emerald-800">
                    <p.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0D5C46] py-20 text-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold tracking-[0.22em] text-emerald-300">OUR COMMITMENT</p>
            <p className="mt-5 max-w-3xl font-display text-xl font-semibold leading-relaxed sm:text-2xl">
              We aim to provide a clear, dependable online experience. We review catalogue details before publication
              and work to keep product information accurate. Product availability, delivery options and supply
              eligibility may vary by location and applicable requirements.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/products"
                data-testid="about-browse-products-btn"
                className="group flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-display text-sm font-bold text-[#0D5C46] transition-[background-color,transform] hover:bg-emerald-200 active:scale-95"
              >
                Browse the Catalogue
                <ArrowRight className="h-4 w-4 transition-[transform] group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="about-contact-btn"
                className="rounded-full border border-white/25 px-7 py-3.5 font-display text-sm font-bold text-white transition-[border-color,background-color] hover:border-emerald-300/60 hover:bg-emerald-400/10"
              >
                Contact Our Team
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
